import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/SectionDivider";
import { Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSubmitted(true);
      setForm(initial);
      toast.success("Enquiry received. Our team will reach out privately.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Unable to submit at this moment.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#080808] py-24 md:py-32 overflow-hidden"
    >
      {/* Decorative arc */}
      <svg
        className="absolute -left-32 -bottom-32 w-[520px] h-[520px] opacity-[0.05] pointer-events-none"
        viewBox="0 0 200 200"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="100" cy="100" r="95" stroke="#C9960C" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="75" stroke="#C9960C" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="55" stroke="#C9960C" strokeWidth="0.5" />
      </svg>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-20 items-start">
          <Reveal>
            <Eyebrow testId="contact-eyebrow">Get In Touch</Eyebrow>
            <h2
              data-testid="contact-heading"
              className="font-serif-display text-[#C9960C] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
            >
              Ready to Deploy Your Wealth Globally?
            </h2>
            <p className="mt-6 max-w-xl text-[#F8F6F0]/75 text-base sm:text-lg leading-relaxed">
              Speak with our team to understand how The Global Vault can work for your family. Every conversation is handled with complete discretion.
            </p>

            <div className="mt-12 space-y-6 text-sm">
              <div className="flex items-start gap-4">
                <span className="text-[#C9960C] text-[11px] tracking-[0.3em] uppercase w-24 pt-1">
                  Enquiries
                </span>
                <a
                  href="mailto:private@theglobalvault.in"
                  data-testid="contact-email-link"
                  className="text-[#F8F6F0]/85 hover:text-[#C9960C] transition-colors"
                >
                  private@theglobalvault.in
                </a>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#C9960C] text-[11px] tracking-[0.3em] uppercase w-24 pt-1">
                  Phone
                </span>
                <a
                  href="tel:+919930007347"
                  data-testid="contact-phone-link"
                  className="text-[#F8F6F0]/85 hover:text-[#C9960C] transition-colors"
                >
                  +91 99300 07347
                </a>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#C9960C] text-[11px] tracking-[0.3em] uppercase w-24 pt-1">
                  Hours
                </span>
                <span className="text-[#F8F6F0]/85">By appointment only</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-[#C9960C] text-[11px] tracking-[0.3em] uppercase w-24 pt-1">
                  Coverage
                </span>
                <span className="text-[#F8F6F0]/85">
                  Mumbai · Delhi · Bengaluru · Dubai · Singapore
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#111111] border border-[#C9960C]/40 border-t-2 border-t-[#C9960C] p-8 md:p-10"
            >
              {submitted ? (
                <div className="py-10 text-center" data-testid="contact-success">
                  <div className="font-serif-display text-[#F5D485] text-3xl mb-3">Thank you.</div>
                  <p className="text-[#F8F6F0]/75 leading-relaxed">
                    Your enquiry has been received. A member of our team will respond privately within 1 business day.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-new-enquiry-btn"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 inline-flex items-center gap-2 text-[#C9960C] hover:text-[#F5D485] uppercase tracking-[0.2em] text-xs border-b border-[#C9960C]/50 pb-1 transition-colors cursor-pointer"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <div className="space-y-7">
                  <Field
                    label="Full Name"
                    name="name"
                    testId="contact-input-name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Mr Arjun Mehta"
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    testId="contact-input-email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@privatemail.com"
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    testId="contact-input-phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 98xxxxxxxx"
                  />
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-[10px] tracking-[0.3em] uppercase text-[#C9960C] mb-2"
                    >
                      Your Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us briefly about your goals…"
                      className="w-full bg-transparent border-b border-[#C9960C]/40 text-[#F8F6F0] focus:border-[#C9960C] focus:outline-none py-3 px-0 resize-none transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#080808] border border-[#C9960C] text-[#C9960C] px-8 py-4 uppercase tracking-[0.22em] text-xs font-medium hover:bg-[#C9960C] hover:text-[#080808] transition-colors duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Submitting
                      </>
                    ) : (
                      <>
                        Submit Enquiry
                        <ArrowRight size={14} />
                      </>
                    )}
                  </button>

                  <p className="text-[11px] text-[#F8F6F0]/50 italic text-center pt-2">
                    Strictly Private &amp; Confidential — all enquiries handled with complete discretion.
                  </p>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", placeholder, testId }) => (
  <div>
    <label
      htmlFor={name}
      className="block text-[10px] tracking-[0.3em] uppercase text-[#C9960C] mb-2"
    >
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full bg-transparent border-b border-[#C9960C]/40 text-[#F8F6F0] focus:border-[#C9960C] focus:outline-none py-3 px-0 transition-colors"
    />
  </div>
);

export default Contact;
